import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, TrendingUp, TrendingDown, Minus, Loader2,
  Headphones, Mic, BookOpen, PenTool, BarChart3, Calendar, Target,
  Sparkles, Lightbulb,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import ViewModeToggle, { useViewMode } from '@/components/student/ViewModeToggle';
import { translateProgressForStudent } from '@/utils/studentFriendlyTranslator';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_CONFIG = {
  listening: { icon: Headphones, color: '#2563eb', label: 'Listening' },
  speaking: { icon: Mic, color: '#9333ea', label: 'Speaking' },
  reading: { icon: BookOpen, color: '#16a34a', label: 'Reading' },
  writing: { icon: PenTool, color: '#ea580c', label: 'Writing' },
};

const WIDA_LEVELS = {
  1: { name: 'Entering', color: '#dc2626' },
  2: { name: 'Emerging', color: '#ea580c' },
  3: { name: 'Developing', color: '#ca8a04' },
  4: { name: 'Expanding', color: '#2563eb' },
  5: { name: 'Bridging', color: '#16a34a' },
  6: { name: 'Reaching', color: '#059669' },
};

const TrendIcon = ({ value }) => {
  if (value > 0) return <TrendingUp size={16} className="text-green-600" />;
  if (value < 0) return <TrendingDown size={16} className="text-red-600" />;
  return <Minus size={16} className="text-gray-400" />;
};

const MiniChart = ({ points, color, height = 60 }) => {
  if (!points || points.length < 2) return null;
  
  const scores = points.map(p => p.score);
  const min = Math.min(...scores, 1);
  const max = Math.max(...scores, 6);
  const range = max - min || 1;
  const w = 200;
  
  const pathPoints = scores.map((s, i) => {
    const x = (i / (scores.length - 1)) * w;
    const y = height - ((s - min) / range) * (height - 8);
    return `${x},${y}`;
  });
  
  return (
    <svg width={w} height={height} className="mt-2">
      <polyline
        points={pathPoints.join(' ')}
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {scores.map((s, i) => {
        const x = (i / (scores.length - 1)) * w;
        const y = height - ((s - min) / range) * (height - 8);
        return <circle key={i} cx={x} cy={y} r="4" fill={color} />;
      })}
    </svg>
  );
};

const StudentProgress = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useViewMode('student');

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await axios.get(`${API}/student/progress`);
        setData(res.data);
      } catch (e) {
        if (e.response?.status === 401) {
          toast.error('Please log in to view your progress.');
        } else {
          toast.error('Failed to load progress data.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const { progress = [], domain_trends = {}, summary = {} } = data || {};

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="student-progress-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Link to={user?.role === 'student' ? '/my-assessment' : '/results'}>
          <Button variant="ghost" size="sm" className="h-8 px-2">
            <ArrowLeft size={18} />
          </Button>
        </Link>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="progress-title">
          My Progress
        </h1>
      </div>
      <p className="text-sm text-gray-500 mb-6 ml-11">
        Track your language proficiency growth over time
      </p>

      {/* v6.36.22 — Student / Teacher View toggle */}
      {progress.length > 0 && (
        <div className="flex justify-end mb-6">
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        </div>
      )}

      {/* v6.36.22 — Student-friendly summary (rule-based translation) */}
      {viewMode === 'student' && progress.length > 0 && (() => {
        const t = translateProgressForStudent({
          gradeLevel: user?.grade_level,
          summary,
        });
        return (
          <div className="border-2 border-[#002FA7] p-6 mb-8" data-testid="student-friendly-progress">
            <div className="flex items-start gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-[#002FA7] flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="text-xl font-bold text-[#002FA7]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t.headline}
                </h2>
                <p className="text-xs text-gray-500 mt-1">{t.levelLabel}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              {['listening', 'speaking', 'reading', 'writing'].map((m) => {
                const Icon = DOMAIN_CONFIG[m].icon;
                return (
                  <div key={m} className="flex items-start gap-2 p-3 bg-gray-50" data-testid={`student-can-do-${m}`}>
                    <Icon size={18} style={{ color: DOMAIN_CONFIG[m].color }} className="flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase">{DOMAIN_CONFIG[m].label}</div>
                      <p className="text-sm text-gray-800">{t.canDoByModality[m]}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {t.growthLine && (
              <div className="flex items-start gap-2 p-3 bg-green-50 border-l-4 border-green-500 mb-3" data-testid="student-growth-line">
                <TrendingUp size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-800"><span className="font-semibold">Your growth:</span> {t.growthLine}</p>
              </div>
            )}

            {t.nextStepLine && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border-l-4 border-amber-500" data-testid="student-next-step">
                <Lightbulb size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-800"><span className="font-semibold">Try this next:</span> {t.nextStepLine}</p>
              </div>
            )}
          </div>
        );
      })()}

      {/* Empty State */}
      {progress.length === 0 && (
        <div className="text-center py-16 border-2 border-dashed border-gray-200" data-testid="no-progress">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <h3 className="font-semibold text-gray-500 mb-1">No Assessment History</h3>
          <p className="text-sm text-gray-400 mb-4">
            Complete your first practice exam to start tracking your progress!
          </p>
          <Link to={user?.role === 'student' ? '/my-assessment' : '/grades'}>
            <Button className="btn-sharp-primary" data-testid="start-exam-btn">
              <BookOpen size={16} className="mr-2" /> Take a Practice Exam
            </Button>
          </Link>
        </div>
      )}

      {progress.length > 0 && viewMode === 'teacher' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-t border-l border-gray-300 mb-8" data-testid="progress-summary">
            <div className="border-b border-r border-gray-300 p-5">
              <Calendar className="w-6 h-6 text-gray-400 mb-2" />
              <div className="text-2xl font-bold">{summary.total_exams}</div>
              <div className="text-xs text-gray-500">Exams Taken</div>
            </div>
            <div className="border-b border-r border-gray-300 p-5">
              <Target className="w-6 h-6 mb-2" style={{ color: WIDA_LEVELS[summary.latest_wida_level]?.color || '#666' }} />
              <div className="text-2xl font-bold" style={{ color: WIDA_LEVELS[summary.latest_wida_level]?.color }}>
                L{summary.latest_wida_level}
              </div>
              <div className="text-xs text-gray-500">{summary.latest_wida_name}</div>
            </div>
            <div className="border-b border-r border-gray-300 p-5">
              <div className="flex items-center gap-1 mb-2">
                <TrendIcon value={summary.overall_change} />
              </div>
              <div className={`text-2xl font-bold ${summary.overall_change > 0 ? 'text-green-600' : summary.overall_change < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                {summary.overall_change > 0 ? '+' : ''}{summary.overall_change}
              </div>
              <div className="text-xs text-gray-500">Score Change</div>
            </div>
            <div className="border-b border-r border-gray-300 p-5">
              <div className="text-sm font-semibold text-green-600 capitalize mb-1">{summary.strongest_domain}</div>
              <div className="text-sm font-semibold text-red-500 capitalize">{summary.weakest_domain}</div>
              <div className="text-xs text-gray-500 mt-1">Strongest / Weakest</div>
            </div>
          </div>

          {/* Domain Trend Cards */}
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Domain Progress
          </h2>
          <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="domain-trends">
            {['listening', 'speaking', 'reading', 'writing'].map(domain => {
              const config = DOMAIN_CONFIG[domain];
              const DomainIcon = config.icon;
              const points = domain_trends[domain] || [];
              const latest = points.length > 0 ? points[points.length - 1]?.score : 0;
              const first = points.length > 0 ? points[0]?.score : 0;
              const change = Math.round((latest - first) * 10) / 10;
              const level = Math.min(Math.max(Math.round(latest), 1), 6);
              const barPct = Math.min(((latest - 1) / 5) * 100, 100);

              return (
                <div key={domain} className="border-2 border-gray-200 p-4" data-testid={`domain-trend-${domain}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <DomainIcon size={18} style={{ color: config.color }} />
                      <span className="font-semibold">{config.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold" style={{ color: config.color }}>{latest.toFixed(1)}</span>
                      {points.length > 1 && (
                        <span className={`text-xs px-1.5 py-0.5 font-bold ${change > 0 ? 'bg-green-100 text-green-700' : change < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                          {change > 0 ? '+' : ''}{change}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Progress value={barPct} className="h-2 mb-1" />
                  <div className="flex justify-between text-xs text-gray-400 mb-2">
                    <span>L1 Entering</span>
                    <span>L6 Reaching</span>
                  </div>
                  
                  {points.length > 1 && <MiniChart points={points} color={config.color} />}
                </div>
              );
            })}
          </div>

          {/* Assessment Timeline */}
          <h2 className="text-lg font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Assessment Timeline
          </h2>
          <div className="space-y-3" data-testid="progress-timeline">
            {[...progress].reverse().map((entry, idx) => {
              const levelInfo = WIDA_LEVELS[entry.wida_level] || WIDA_LEVELS[1];
              return (
                <Link
                  key={idx}
                  to={`/results/${entry.session_id}`}
                  className="block border-2 border-gray-200 p-4 hover:border-gray-400 transition-colors"
                  data-testid={`timeline-entry-${idx}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className="w-12 h-12 flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: levelInfo.color }}
                      >
                        L{entry.wida_level}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">
                          {entry.exam_type === 'screener' ? 'MOSAIC Screener' : 'MOSAIC Teacher Edition'} — Grade {entry.grade_level}
                        </div>
                        <div className="text-xs text-gray-500">
                          {entry.scored_at ? new Date(entry.scored_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Unknown date'}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold" style={{ color: levelInfo.color }}>
                        {entry.overall_score}
                      </div>
                      <div className="text-xs text-gray-500">{levelInfo.name}</div>
                    </div>
                  </div>
                  {/* Mini domain scores */}
                  <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-100">
                    {['listening', 'speaking', 'reading', 'writing'].map(d => (
                      <div key={d} className="text-center">
                        <div className="text-xs text-gray-400 capitalize">{d}</div>
                        <div className="text-sm font-bold" style={{ color: DOMAIN_CONFIG[d].color }}>
                          {entry[`${d}_wida`]?.toFixed(1) || '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default StudentProgress;
